"use client";

import React, { useEffect } from "react";
import Image from "next/image";
import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { usePathname } from "next/navigation";

const IMAGES = [
  "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma/new/card-1.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma/new/card-2.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma/new/card-3.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma/new/card-4.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma/new/card-5.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma/new/card-6.webp",
];

const CairaSectionLp: React.FC = () => {
  const pathname = usePathname();

  // Hide button on specific URL
  const shouldHideButton =
    pathname === "/best-us-cpa-admission-online-course-fees-in-india";

  // Mobile carousel autoplay
  const [mobileEmblaRef, mobileEmblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: "start",
      dragFree: true,
      containScroll: "trimSnaps",
      slidesToScroll: 1,
      skipSnaps: false,
    },
    [
      Autoplay({
        delay: 2200,
        stopOnInteraction: false,
        stopOnMouseEnter: true,
        stopOnFocusIn: false,
      }),
    ],
  );

  // Desktop carousel autoplay
  const [desktopEmblaRef, desktopEmblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: "start",
      dragFree: true,
      containScroll: "trimSnaps",
      slidesToScroll: 1,
      skipSnaps: false,
    },
    [
      Autoplay({
        delay: 2200,
        stopOnInteraction: false,
        stopOnMouseEnter: true,
        stopOnFocusIn: false,
      }),
    ],
  );

  useEffect(() => {
    if (!mobileEmblaApi) return;

    try {
      const plugins = mobileEmblaApi.plugins();
      const autoplay = plugins?.autoplay;
      if (autoplay && typeof autoplay.play === "function") {
        autoplay.play();
      }
    } catch (error) {
      console.error("Mobile carousel autoplay error:", error);
    }
  }, [mobileEmblaApi]);

  useEffect(() => {
    if (!desktopEmblaApi) return;

    try {
      const plugins = desktopEmblaApi.plugins();
      const autoplay = plugins?.autoplay;
      if (autoplay && typeof autoplay.play === "function") {
        autoplay.play();
      }
    } catch (error) {
      console.error("Desktop carousel autoplay error:", error);
    }
  }, [desktopEmblaApi]);

  return (
    <>
      <div
        className="w-full relative isolate bg-[#061A2F]"
        style={{
          minHeight: shouldHideButton ? "360px" : "390px",
          contain: "layout",
        }}
      >
        {/* Desktop Background */}
        <div className="absolute inset-0 hidden md:block">
          <Image
            src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cma/new/caira-new-bg-desk.webp"
            alt="BG Image"
            fill
            className="object-cover"
            priority
            sizes="(min-width: 768px) 100vw, 0px"
          />
        </div>
        {/* Mobile Background */}
        <div className="absolute inset-0 md:hidden">
          <Image
            src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cma/new/caira-new-bg-mob.webp?tr=w-480,h-560,q-80"
            alt="BG Image"
            fill
            className="object-cover"
            priority
            sizes="(max-width: 767px) 100vw, 0px"
          />
        </div>
        <div className="max-w-[1340px] lg:px-7 md:px-4 mx-auto relative px-0 lg:py-10 z-10">
          <div
            className={`grid grid-cols-1 md:grid-cols-2 md:justify-between md:items-center gap-5 relative md:py-6  ${shouldHideButton ? "pb-6 pt-6" : "py-10"}`}
          >
            <div className="flex flex-col items-center md:items-start gap-4 sm:gap-6 lg:pt-0">
              <div className="md:text-left text-center w-full">
                <h2 className="text-lg leading-[1.2] font-medium text-white md:text-[32px] md:leading-[1.4] md:pt-4">
                  FREE: CAIRA - Level 1 <br className="md:hidden block" />
                  (Certified <br className="md:block hidden" /> AI-Ready
                  Accountant)
                </h2>
                <p className="text-white text-sm md:mt-4 mt-2">
                  Learn AI from Global Accounting Innovators
                </p>

                {/* Mobile Carousel */}
                <div
                  className="md:hidden block w-full relative py-2 mt-5"
                  role="region"
                  aria-label="AI Accountant program showcase"
                >
                  <div className="embla overflow-hidden" ref={mobileEmblaRef}>
                    <div
                      className="embla__container flex"
                      role="group"
                      aria-live="polite"
                    >
                      {IMAGES.map((imageSrc: string, index: number) => (
                        <div
                          key={`slide-${index}`}
                          className="embla__slide flex-[0_0_33.33%] sm:flex-[0_0_25%] min-w-0 pr-2"
                          role="group"
                          aria-label={`Slide ${index + 1} of ${IMAGES.length}`}
                        >
                          <div className="relative cursor-pointer h-auto w-full touch-manipulation">
                            <SingleInstructorUI src={imageSrc} index={index} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop Carousel - Breaking out to right edge */}
            <div
              className="hidden md:block relative py-2"
              style={{
                width: "calc(100% + ((100vw - 1340px) / 2) + 28px)",
                marginRight: "calc(-1 * ((100vw - 1340px) / 2) - 28px)",
              }}
              role="region"
              aria-label="AI Accountant program showcase"
            >
              <div className="embla overflow-hidden" ref={desktopEmblaRef}>
                <div
                  className="embla__container flex"
                  role="group"
                  aria-live="polite"
                >
                  {IMAGES.map((imageSrc: string, index: number) => (
                    <div
                      key={`desktop-slide-${index}`}
                      className="embla__slide flex-shrink-0 pr-3"
                      role="group"
                      aria-label={`Slide ${index + 1} of ${IMAGES.length}`}
                    >
                      <div className="relative cursor-pointer touch-manipulation">
                        <SingleInstructorUI
                          src={imageSrc}
                          index={index}
                          isDesktop
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default CairaSectionLp;

function SingleInstructorUI({
  src,
  index,
  ariaHidden,
  isDesktop,
}: {
  src: string;
  index: number;
  ariaHidden?: boolean;
  isDesktop?: boolean;
}) {
  // Apply ImageKit transformation for optimized image delivery
  // Desktop: 154x275, Mobile: 130x230 (actual displayed size on mobile is ~129x230)
  const optimizedSrc = isDesktop
    ? `${src}?tr=w-154,h-275,q-60`
    : `${src}?tr=w-130,h-230,q-60`;

  return (
    <article
      className={`shrink-0 overflow-hidden rounded-[4px] bg-[#0b0b0b] transition-transform hover:scale-105 ${
        isDesktop ? "w-[154px] h-[275px]" : "w-full h-[200px] sm:h-[280px]"
      }`}
      aria-hidden={ariaHidden ? "true" : undefined}
    >
      <div className="relative w-full h-full">
        <Image
          src={optimizedSrc}
          alt={`AI Accountant program showcase ${index + 1}`}
          fill
          className="object-cover transition-transform"
          priority={index < 3}
          loading={index < 3 ? "eager" : "lazy"}
          sizes={isDesktop ? "154px" : "130px"}
        />
      </div>
    </article>
  );
}
